import React, { useState } from 'react';
import { CopyIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';

function TokenForm({ mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const [token, setToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGenerate = async () => {
        try {
            setIsSubmitting(true);
            const { data } = await server.post('club/api', {});
            enqueueSnackbar('Token gerado com sucesso.');
            setToken(data?.token?.token || '');
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (!token) return;
        navigator.clipboard.writeText(token);
        enqueueSnackbar('Token copiado para área de transferência!');
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Adicionar token</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <Alert>
                        <AlertTitle>Informação</AlertTitle>
                        <AlertDescription>Guarde esse token em um local seguro — não será possível recuperá-lo.</AlertDescription>
                    </Alert>

                    {token ? (
                        <div className="flex flex-col gap-2">
                            <span className="text-sm text-muted-foreground">Token gerado:</span>
                            <div className="flex gap-2">
                                <Input value={token} readOnly className="font-mono text-xs" />
                                <Button type="button" variant="outline" size="icon" onClick={handleCopy} title="Copiar token">
                                    <CopyIcon className="size-4" />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Alert>
                            <AlertDescription>Clique em &quot;Gerar token&quot; para criar um novo token de API.</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    {!token ? (
                        <Button type="button" onClick={handleGenerate} disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Gerar token'}
                        </Button>
                    ) : (
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Fechar
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function DialogToken({ mutate }) {
    const { open, handleClose } = useDialog('#tokens');

    if (!open) return null;

    return (
        <TokenForm
            mutate={mutate}
            open={open}
            onOpenChange={isOpen => {
                if (!isOpen) handleClose();
            }}
        />
    );
}
